import { createRouter, createWebHistory } from 'vue-router'
import { getCurrentUser } from 'vuefire'
import { logger } from './utils/logger'

// Dynamic imports for AdminLayout and Dashboard for consistency
const AdminLayout = () => import('./layouts/AdminLayout.vue')
const Dashboard = () => import('./views/MainDashboard.vue')

const router = createRouter({
  history: createWebHistory('/'),
  routes: [
    {
      path: '/login',
      name: 'Login',
      component: () => import('./views/LoginForm.vue'),
      meta: {
        requiresAuth: false,
        title: 'Login',
        description: 'Login to your account'
      }
    },
    {
      // The browser half of the desktop sign-in handoff. Deliberately outside
      // AdminLayout: it is a single decision, not a place in the dashboard, and
      // it handles its own sign-in so the query parameters survive it.
      path: '/connect',
      name: 'Connect',
      component: () => import('./views/ConnectApp.vue'),
      meta: {
        requiresAuth: false,
        title: 'Connect an application',
        description: 'Authorize a desktop application to use your DMG account'
      }
    },
    {
      path: '/',
      component: AdminLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: '',
          name: 'Dashboard',
          component: Dashboard,
          meta: {
            title: 'Dashboard',
            description: {
              what: 'Your home base. A quick-glance overview of all the tools available in DMG Internal Tools, organized by category.',
              dataSource: 'This page is a navigation hub — it doesn\'t pull live data. It links to all the other tools.',
              usage: 'Click any card to jump straight to that tool. Use the sidebar on the left to navigate between sections at any time.'
            }
          }
        },
        {
          path: 'metrics',
          name: 'Metrics',
          children: [
            {
              path: 'web',
              name: 'WebMetrics',
              component: () => import('./views/Metrics/MetricsView.vue'),
              props: { metricsType: 'web' },
              meta: {
                title: 'Web Metrics',
                description: {
                  what: 'Shows how your websites are performing in Google Search — things like how many clicks you\'re getting, how often your pages show up in results, your click-through rate, and average ranking position.',
                  dataSource: 'The data comes from Google Search Console and is stored in BigQuery (Google\'s data warehouse). It covers web search results and can go back several years.',
                  usage: 'Pick a site (or multiple sites), choose a date range, and see your performance over time. You can compare against a previous period or last year, filter by device type, and break things down by search type.'
                }
              }
            },
            {
              path: 'discover',
              name: 'DiscoverMetrics',
              component: () => import('./views/Metrics/MetricsView.vue'),
              props: { metricsType: 'discover' },
              meta: {
                title: 'Discover Metrics',
                description: {
                  what: 'Shows how your content is performing in Google Discover — the personalized news feed that appears on Android phones and the Google app. Tracks clicks, impressions, and click-through rate.',
                  dataSource: 'The data comes from Google Search Console\'s Discover reports, stored in BigQuery. Discover data is typically available for the last 16 months.',
                  usage: 'Select a site and date range to see how your Discover traffic is trending. Compare periods to spot changes, and filter by category to see which topics perform best.'
                }
              }
            },
            {
              path: 'site-categories',
              name: 'SiteCategories',
              component: () => import('./views/Metrics/SiteCategories.vue'),
              meta: {
                title: 'Site Categories',
                description: {
                  what: 'A tool for organizing your website URLs into categories (like "News", "Reviews", "How-to guides"). These categories are used throughout the app to let you filter and analyze performance by content type.',
                  dataSource: 'Category assignments are stored in the app\'s database. The URL list comes from your verified sites in Search Console.',
                  usage: 'Browse your URLs, assign them to categories, create new categories, or bulk-import category mappings. Changes here affect how data is grouped on other pages.'
                }
              }
            },
            {
              path: 'category-explorer',
              name: 'CategoryExplorer',
              component: () => import('./views/Metrics/CategoryExplorer.vue'),
              meta: {
                title: 'Category Explorer',
                description: {
                  what: 'Lets you see how different content categories are performing side-by-side. Great for answering questions like "Is our News section growing faster than Reviews?"',
                  dataSource: 'Combines your Search Console performance data (from BigQuery) with the category assignments you\'ve set up in Site Categories.',
                  usage: 'Pick a site and time period, then explore metrics broken down by category. Compare current performance against a previous period to spot trends and shifts.'
                }
              }
            }
          ]
        },
        {
          path: 'intelligence',
          name: 'Intelligence',
          children: [
            {
              path: 'ai-crawlers',
              name: 'AICrawlerAdoption',
              component: () => import('./views/Metrics/RobotsMonitor.vue'),
              meta: {
                title: 'AI Crawler Adoption',
                description: {
                  what: 'Tracks which AI crawlers (like GPTBot, ClaudeBot, and Google-Extended) major news publishers block in their robots.txt file, whether they publish an llms.txt, and which sitemaps they declare. Shows how publisher attitudes toward AI crawling shift over time.',
                  dataSource: 'Every Monday a background job fetches each publisher\'s robots.txt and llms.txt directly from their website, parses the rules, and stores a compact weekly snapshot in the app\'s database. No third-party data — it reads the public files the publishers themselves publish.',
                  usage: 'Use "Adoption Over Time" to see the trend of who blocks which crawler. "Leaderboard" ranks the most-blocked crawlers and most-restrictive publishers. "Weekly Changes" lists what changed in the latest run. "Publishers" lets you manage the tracked site list and trigger a manual run with "Run now". "User-agents" lets you add, rename or delete the crawlers being tracked; changes save immediately and apply from the next run. Matching ignores case (gptbot in a robots.txt counts as GPTBot), but the spelling you save is what the charts show, so changing only the case starts a new line in the charts.'
                }
              }
            }
          ]
        },
        {
          path: 'search-console',
          component: () => import('./views/SearchConsole/Index.vue'),
          name: 'SearchConsole',
          children: [
            {
              path: 'query',
              name: 'SearchConsoleQuery',
              component: () => import('./views/Metrics/SearchConsole.vue'),
              meta: {
                title: 'Search Console Query Builder',
                description: {
                  what: 'A flexible tool for pulling custom reports from Search Console. Instead of the pre-built dashboards, you control exactly what data you see — which queries, pages, countries, and date ranges.',
                  dataSource: 'Connects directly to the Google Search Console API in real time. Results reflect the latest available data from Google (usually 2-3 days behind).',
                  usage: 'Set your filters (site, date range, dimensions like query or page), then run the report. Results appear in a sortable table that you can export. Great for deep-dive investigations.'
                }
              }
            },
            {
              path: 'hourly',
              name: 'SearchConsoleHourly',
              component: () => import('./views/Metrics/SearchConsoleHourly.vue'),
              meta: {
                title: 'Search Console Hourly Data',
                description: {
                  what: 'Shows Search Console data broken down by hour instead of by day. Useful for spotting sudden traffic drops or spikes as they happen, rather than waiting for the daily summary.',
                  dataSource: 'Uses Google Search Console\'s hourly data export, which provides granular performance data for roughly the last 10 days.',
                  usage: 'Select a site and date to see hourly click and impression patterns. Use this to detect issues quickly — if traffic drops at 2 PM, you\'ll see it here before the daily data catches up.'
                }
              }
            },
            {
              path: 'merlin',
              name: 'Merlin',
              component: () => import('./views/Metrics/Merlin.vue'),
              meta: {
                title: 'Merlin - Traffic Diagnostic',
                description: {
                  what: 'A diagnostic tool that helps you understand why your search traffic went up or down. It compares two time periods and pinpoints which queries, pages, or countries drove the change.',
                  dataSource: 'Pulls data from the Google Search Console API, comparing the two time windows you select.',
                  usage: 'Pick a site, set a "before" and "after" date range, then run the analysis. Merlin breaks down the traffic change into contributing factors — so instead of just seeing "clicks dropped 20%", you\'ll see exactly which queries lost traffic.'
                }
              }
            }
          ]
        },
    
        {
          path: 'settings',
          component: () => import('./views/Settings/Index.vue'),
          children: [
            {
              path: 'pipeline',
              name: 'SettingsPipeline',
              component: () => import('./views/Settings/Pipeline.vue'),
              meta: {
                title: 'Pipeline',
                description: {
                  what: 'Controls the automated jobs that pull fresh data from Google Search Console into the system. Think of it as the "engine room" — it keeps your metrics up to date without manual work.',
                  dataSource: 'Manages scheduled data imports from the Search Console API into BigQuery. Shows job status, run history, and any errors.',
                  usage: 'View which data pipelines are running, check if any jobs have failed, trigger a manual data refresh, or adjust the schedule. You usually won\'t need to touch this unless something breaks.'
                }
              }
            },
            {
              path: 'verified-sites',
              name: 'VerifiedSites',
              component: () => import('./views/Settings/VerifiedSites.vue'),
              meta: {
                title: 'Verified Sites',
                description: {
                  what: 'Shows all the websites that have been connected and verified with this tool. Only verified sites can have their Search Console data pulled and analyzed.',
                  dataSource: 'The list of sites comes from your Google Search Console account. Verification status is checked against Google\'s API.',
                  usage: 'Review which sites are connected, add new sites to monitor, or remove ones you no longer need. If a site isn\'t showing up in the metrics pages, check here first.'
                }
              }
            },
            {
              path: 'discover-export',
              name: 'DiscoverExport',
              component: () => import('./views/Metrics/DiscoverDownload.vue'),
              meta: {
                title: 'Discover Export',
                description: {
                  what: 'Lets you download your Google Discover performance data as a spreadsheet (CSV file). Includes page-level details like URLs, click counts, and how content appeared in Discover.',
                  dataSource: 'Pulls from the Google Search Console Discover API. The export includes data for whichever date range and sites you select.',
                  usage: 'Choose a site and date range, then click export. The downloaded CSV can be opened in Excel or Google Sheets for further analysis or sharing with your team.'
                }
              }
            },
            {
              path: 'configuration',
              name: 'Configuration',
              component: () => import('./views/Settings/Configuration.vue'),
              meta: {
                title: 'Configuration',
                description: {
                  what: 'System-wide settings for DMG Internal Tools. Controls things like your profile, connected accounts, and general preferences.',
                  dataSource: 'Settings are stored in the app\'s database and apply to your user account.',
                  usage: 'Update your profile information, manage connected Google accounts, or adjust app-wide preferences. Changes here apply immediately.'
                }
              }
            },
            {
              path: 'data-management',
              name: 'DataManagement',
              component: () => import('./views/Settings/DataManagement.vue'),
              meta: {
                title: 'Data Management',
                description: {
                  what: 'Tools for backing up and restoring your cached metrics data. Useful if you want to save a snapshot of your data or move it between environments.',
                  dataSource: 'Works with the locally cached data that the app stores for faster page loads. This is a copy of data already in BigQuery.',
                  usage: 'Export your cached data as a backup file, or import a previously saved backup. Helpful for troubleshooting or when setting up the tool on a new machine.'
                }
              }
            },
            {
              path: 'algorithm-updates',
              name: 'AlgorithmUpdates',
              component: () => import('./views/Settings/AlgorithmUpdates.vue'),
              meta: {
                title: 'Algorithm Updates',
                description: {
                  what: 'A timeline of confirmed Google Search algorithm updates (like core updates, spam updates, and helpful content updates). Helps you correlate traffic changes with known algorithm rollouts.',
                  dataSource: 'Pulled from Google\'s official Search Status Dashboard. Updates are added automatically as Google announces them.',
                  usage: 'Browse the timeline to see when major updates happened. If your traffic changed around a specific date, check here to see if a Google update might explain it.'
                }
              }
            },
            {
              path: 'testing',
              name: 'Testing',
              component: () => import('./views/Settings/Testing.vue'),
              meta: {
                title: 'Testing',
                description: {
                  what: 'Internal testing and diagnostic tools for developers. Used to verify that the app\'s connections and features are working correctly.',
                  dataSource: 'Runs test queries against the app\'s various data sources and APIs.',
                  usage: 'Run diagnostic checks to verify connectivity, test API endpoints, or debug issues. This page is mainly for developers and troubleshooting.'
                }
              }
            },
            {
              path: 'api-keys',
              name: 'ApiKeys',
              component: () => import('./views/Settings/ApiKeys.vue'),
              meta: {
                title: 'API Keys',
                description: {
                  what: 'Manages API keys that let external tools (like Claude Code) connect to this app\'s data. Think of API keys like passwords that allow other software to securely access your metrics.',
                  dataSource: 'API keys are generated and stored in the app\'s database. They grant read access to two things: live Search Console data, and the BigQuery warehouse holding the full history.',
                  usage: 'Create new API keys, copy them for use in other tools, or revoke keys you no longer need. Treat these like passwords — don\'t share them publicly.'
                }
              }
            }

          ]
        },
        {
          path: 'google-trends',
          children: [
            {
              path: 'manage',
              name: 'GoogleTrends',
              component: () => import('./views/Settings/GoogleTrends.vue'),
              meta: {
                title: 'Google Trends',
                description: {
                  what: 'Set up monitoring for topics and keywords in Google Trends. Get automatic email reports showing how search interest is changing over time for the terms you care about.',
                  dataSource: 'Data comes from Google Trends, which shows the relative popularity of search terms over time. It\'s not exact search volume — it\'s a 0-100 interest score.',
                  usage: 'Create monitoring projects by adding keywords you want to track. Set up email recipients to get regular trend reports delivered to your inbox automatically.'
                }
              }
            }
          ]
        }
      ]
    },
    // Add catch-all 404 route
    {
      path: '/:pathMatch(.*)*',
      name: 'NotFound',
      component: () => import('./views/NotFound.vue'),
      meta: {
        title: 'Page Not Found',
        description: 'The requested page could not be found'
      }
    }
  ]
})

// Navigation guard with error handling and centralized title setting
router.beforeEach(async (to, from, next) => {
  try {
    const currentUser = await getCurrentUser()
    const requiresAuth = to.meta.requiresAuth !== false

    // Centralized document title setting
    setDocumentTitle(to.meta.title)

    if (requiresAuth && !currentUser) {
      next({ name: 'Login' })
    } else if (currentUser && to.name === 'Login') {
      next({ name: 'Dashboard' })
    } else {
      next()
    }
  } catch (error) {
    logger.error("Error during navigation:", error);
    next(false); // halt navigation on error
  }
})

function setDocumentTitle(title) {
  document.title = title 
    ? `DMG Internal Tools - ${title}` 
    : 'DMG Internal Tools'
}

export default router 